import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Star, Plus, Heart, ChevronDown } from 'lucide-react';
import { formatVND, slugify } from '../../utils/formatters';
import { getAllProducts } from '../../api/productApi/ProductApi';
import { categoryService } from '../../components/services/categoryService';

export default function CategoryProductsPage({ addToCart }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(8); // Ban đầu hiển thị 8 sản phẩm giống trang Home
  const [categoryName, setCategoryName] = useState('Danh Mục Sản Phẩm');
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);

useEffect(() => {
    const fetchCategoryAndProducts = async () => {
      setLoading(true);
      setVisibleCount(8); // Reset lại số lượng hiển thị khi đổi danh mục
      try {
        const catRes = await categoryService.getAllCategories();
        const categories = Array.isArray(catRes) ? catRes : (catRes.data || []);
        
        // Hàm đệ quy vét cạn tất cả ID và slug của danh mục cha, con, cháu...
        const collectCategoryIdsAndSlugs = (category, idSet, slugSet) => {
          if (!category) return;
          
          if (category.id !== undefined && category.id !== null) {
            idSet.add(String(category.id));
          }
          if (category.name) {
            slugSet.add(slugify(category.name));
          }

          // Kiểm tra nếu có danh mục con (subCategories hoặc children)
          const children = category.subCategories || category.children || [];
          if (Array.isArray(children) && children.length > 0) {
            children.forEach(child => {
              collectCategoryIdsAndSlugs(child, idSet, slugSet);
            });
          }
        };

        // Tìm danh mục gốc trên URL khớp với slug hoặc ID
        const currentCat = categories.find(c => slugify(c.name) === slug || String(c.id) === slug);
        
        let validCategoryIds = new Set();
        let validCategorySlugs = new Set();

        if (currentCat) {
          setCategoryName(currentCat.name);
          // Gọi đệ quy quét sạch toàn bộ nhánh con cháu bên trong
          collectCategoryIdsAndSlugs(currentCat, validCategoryIds, validCategorySlugs);
        } else {
          // Trường hợp không tìm thấy trong cây, dùng tạm slug trên URL
          validCategoryIds.add(slug);
          validCategorySlugs.add(slug);
        }

        // Lấy tất cả sản phẩm từ hệ thống
        const res = await getAllProducts(0, 300); // Lấy số lượng lớn để gom đủ sản phẩm
        const allProducts = res?.data?.data || res?.data || [];

        // Lọc sản phẩm thuộc bất kỳ cấp độ nào (cha, con, cháu) trong nhánh đó
        const filtered = allProducts.filter(p => {
          const pCatId = String(p.categoryId ?? p.category?.id ?? p.category_id ?? '');
          const pCatNameSlug = slugify(p.categoryName || p.category?.name || '');

          return validCategoryIds.has(pCatId) || validCategorySlugs.has(pCatNameSlug);
        });

        setProducts(filtered);
      } catch (error) {
        console.error("Lỗi tải sản phẩm theo danh mục:", error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCategoryAndProducts();
    }
  }, [slug]);

  const toggleWishlist = (productId) => {
    setWishlist((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  // Lấy ra danh sách sản phẩm cắt theo số lượng đang hiển thị
  const displayedProducts = products.slice(0, visibleCount);
  const hasMore = visibleCount < products.length;

  return (
    <div className="min-h-screen bg-slate-50/50 py-10 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Nút quay lại */}
        <div>
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-indigo-600 bg-white hover:bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-2xl transition shadow-sm cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" /> Quay lại
          </button>
        </div>

        {/* Header tiêu đề danh mục */}
        <div className="bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-8 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
              Khám phá bộ sưu tập
            </span>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-serif mt-2">
              {categoryName}
            </h1>
          </div>
          <div className="text-xs text-slate-500 font-medium">
            Hiển thị <strong className="text-slate-900">{displayedProducts.length}</strong> / {products.length} sản phẩm
          </div>
        </div>

        {/* Danh sách sản phẩm */}
        {loading ? (
          <div className="text-center py-20 text-slate-400 text-sm">Đang tải sản phẩm theo danh mục...</div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-bold text-slate-800 text-base">Chưa có sản phẩm nào trong danh mục này</h3>
            <p className="text-xs text-slate-400 mt-1">Vui lòng quay lại sau khi cửa hàng cập nhật thêm sản phẩm.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {displayedProducts.map((product) => {
                const isWish = wishlist.includes(product.id || product.realId);
                const imgUrl = product.thumbnail || product.images?.[0] || 'https://via.placeholder.com/500';
                const stockVal = Number(product?.stock ?? product?.quantity ?? 1);
                const isOutOfStock = stockVal <= 0;

                return (
                  <div
                    key={product.id || product.realId}
                    className="group bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                  >
                    <div className="relative bg-slate-100 aspect-square overflow-hidden flex items-center justify-center">
                      <img
                        src={imgUrl}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                      />
                      {isOutOfStock ? (
                        <span className="absolute top-3 left-3 bg-rose-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                          HẾT HÀNG
                        </span>
                      ) : (
                        <span className="absolute top-3 left-3 bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                          CÒN HÀNG
                        </span>
                      )}

                      <div className="absolute top-3 right-3 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => toggleWishlist(product.id || product.realId)}
                          className={`p-2 bg-white/90 hover:bg-white text-slate-700 rounded-full shadow-md transition ${isWish ? 'text-rose-500' : ''}`}
                        >
                          <Heart className={`w-4 h-4 ${isWish ? 'fill-rose-500' : ''}`} />
                        </button>
                      </div>
                    </div>

                    <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                      <div>
                        <div className="flex items-center space-x-1 text-amber-400 text-xs mb-1">
                          <Star className="w-3.5 h-3.5 fill-amber-400" />
                          <span className="font-bold text-slate-800">{product.star || 5}</span>
                        </div>
                        <h3 className="font-bold text-slate-900 text-sm group-hover:text-indigo-600 transition line-clamp-2">
                          {product.name}
                        </h3>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-50">
                        <div className="text-base font-extrabold text-slate-900">{formatVND(product.price ?? 0)}</div>
                        <button
                          type="button"
                          disabled={isOutOfStock}
                          onClick={() => !isOutOfStock && addToCart && addToCart(product)}
                          className={`p-2.5 rounded-2xl transition shadow-md flex items-center justify-center ${
                            isOutOfStock
                              ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                              : 'bg-slate-900 hover:bg-indigo-600 text-white cursor-pointer'
                          }`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Nút Xem Thêm / Load More */}
            {hasMore && (
              <div className="text-center pt-8">
                <button
                  onClick={() => setVisibleCount((prev) => prev + 8)}
                  className="px-8 py-3.5 bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 rounded-2xl text-xs font-bold transition shadow-sm inline-flex items-center gap-2 cursor-pointer"
                >
                  <span>Xem thêm sản phẩm</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}